import { StackScreenProps } from "@react-navigation/stack";
import { ProductActionsSelectVariantTemplateRootParamList } from "../../../../types";
import { useScheduler } from "../../../../utilities/scheduler";
import { QUERY_PRODUCT_OPTION_GROUP_TEMPLATES } from "../../utils";
import { useLoadMoreList } from "../../../../utilities/load-more-list";
import {
  Product,
  ProductOptionGroupTemplate,
} from "@towersystems/roam-common/lib/generated-types";
import { useSearch } from "../../../../utilities/search";
import React from "react";
import {
  DEFAULT_PAGE_WIDTH,
  Divider,
  FlatListSearchable,
  Page,
  Text,
} from "@towersystems/roam-common-native";
import { FlatListSearchableSkeleton } from "../../../../components/FlatListSearchableSkeleton";
import { Spacings, TouchableOpacity, View } from "react-native-ui-lib";
import { useFocusEffect } from "@react-navigation/native";
import { toTitleCase } from "@towersystems/roam-common/lib/shared-utils";
import { useTranslation } from "../../../../utilities/i18n";

export type ProductActionsSelectVariantTemplateScreenProps = StackScreenProps<
  ProductActionsSelectVariantTemplateRootParamList,
  "ProductActionsSelectVariantTemplateScreen"
>;

export const ProductActionsSelectVariantTemplateScreen = ({
  navigation,
}: ProductActionsSelectVariantTemplateScreenProps) => {
  const { t } = useTranslation("product");

  const { schedule } = useScheduler(500);
  const search = useSearch();

  const loadMoreList = useLoadMoreList<ProductOptionGroupTemplate>({
    initialLoad: false,
    query: QUERY_PRODUCT_OPTION_GROUP_TEMPLATES,
    resultKey: "productOptionGroupTemplates",
    sort: { updatedAt: "DESC" },
    variableFormatter: (variables) => {
      return {
        filterOperator: "AND",
        options: variables,
      };
    },
  });

  React.useEffect(() => {
    if (search.touched) {
      schedule(() => handleLoadMore(true));
    }
  }, [search.query, search.touched]);

  useFocusEffect(
    React.useCallback(() => {
      loadMoreList.reset();
      handleLoadMore(true);
    }, [])
  );

  const handleLoadMore = React.useCallback(
    (reset?: boolean) => {
      loadMoreList.loadMore(
        {
          filter: {
            ...(search.query.length > 0 && {
              name: {
                contains: search.query,
              },
            }),
          },
        },
        reset
      );
    },
    [search.query, loadMoreList.loadMore]
  );

  const items: ProductOptionGroupTemplate[] = loadMoreList.items || [];

  const emptyComponentMarkup = (
    <View>
      <Text>{t("common:empty_list")}</Text>
    </View>
  );

  const renderItem = React.useCallback(
    ({ item }: { item: ProductOptionGroupTemplate }) => {
      return (
        <TouchableOpacity
          onPress={() =>
            navigation.navigate(
              "ProductActionsSelectVariantTemplateOptionValuesRootScreen",
              {
                screen: "ProductActionsSelectVariantTemplateOptionValuesScreen",
                templateId: item.id,
              }
            )
          }
          style={{
            display: "flex",
            justifyContent: "space-between",
            flexDirection: "row",
          }}
        >
          <View row centerV style={{ flexBasis: "100%", flexShrink: 1 }}>
            <Text
              variant="heading"
              fontWeight="medium"
              numberOfLines={1}
              ellipsizeMode="head"
            >
              {toTitleCase(item.name)}
            </Text>
          </View>
        </TouchableOpacity>
      );
    },
    []
  );

  if (loadMoreList.loading && loadMoreList.called == false) {
    return (
      <Page>
        <FlatListSearchableSkeleton searchbar={true} />;
      </Page>
    );
  }

  return (
    <Page scrollable={false} padding={0} fullWidth>
      <FlatListSearchable
        contentContainerStyle={{
          maxWidth: DEFAULT_PAGE_WIDTH,
          width: "100%",
          alignSelf: "center",
          paddingHorizontal: Spacings.s5,
          paddingTop: Spacings.s5,
        }}
        placeholder="Search..."
        onChange={(query) => search.setQuery(query)}
        value={search.query}
        ItemSeparatorComponent={Divider}
        renderItem={renderItem}
        refreshing={loadMoreList.loading}
        data={items as any}
        scrollEnabled={true}
        ListEmptyComponent={emptyComponentMarkup}
        onEndReached={() => {
          items.length >= 1 && handleLoadMore(false);
        }}
      />
    </Page>
  );
};
